import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import {
  CONTENT_MAX_WIDE,
  CONTENT_INSET,
  pageTopPadding,
  sectionPadding,
} from '../styles/layout';
import {
  GUIDES_PATH,
  GUIDES_CHECKOUT_PATH,
  GUIDES_PAGE_TITLE,
  GUIDES_HERO_HEADLINE,
  GUIDES_HERO_SUBHEAD,
  GUIDES_TAGLINE,
  GUIDES_SUPPORT_EMAIL,
  GUIDE_PRODUCTS,
  GUIDE_COMING_SOON_NOTE,
} from '../constants/guides';
import GuideProductCard from '../components/guides/GuideProductCard';
import InflatableInquirySection from '../components/guides/InflatableInquirySection';
import { fetchGuideDownloads } from '../services/guideCheckout';

const GOLD = '#C9A84C';
const BG = '#090909';
const CARD_BG = '#111111';
const TEXT = '#F5F0E8';
const MUTED = 'rgba(245, 240, 232, 0.72)';

const Guides = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [checkoutNotice, setCheckoutNotice] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [downloads, setDownloads] = useState([]);
  const [downloadMeta, setDownloadMeta] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  const [loadingDownloads, setLoadingDownloads] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 900);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('checkout');
    const sid = params.get('session_id') || '';
    const pi = params.get('payment_intent') || '';

    if (status === 'success' || pi || sid) {
      setCheckoutNotice({
        type: 'success',
        text: 'Payment received. Your downloads are below, and a confirmation email is on the way.',
      });
      if (pi) setPaymentIntentId(pi);
      if (sid) setSessionId(sid);
    } else if (status === 'cancelled') {
      setCheckoutNotice({
        type: 'cancelled',
        text: 'Checkout cancelled. You can restart anytime when you are ready.',
      });
    }

    if (status || sid || pi) {
      const url = new URL(window.location.href);
      url.searchParams.delete('checkout');
      url.searchParams.delete('session_id');
      url.searchParams.delete('payment_intent');
      url.searchParams.delete('payment_intent_client_secret');
      window.history.replaceState({}, '', url.pathname + url.hash);
    }
  }, []);

  useEffect(() => {
    if (!paymentIntentId && !sessionId) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoadingDownloads(true);
      setDownloadError('');
      try {
        const data = await fetchGuideDownloads({
          paymentIntentId: paymentIntentId || undefined,
          sessionId: sessionId || undefined,
        });
        if (cancelled) return;
        setDownloads(Array.isArray(data.downloads) ? data.downloads : []);
        setDownloadMeta(data);
        if (data.missingCount > 0 && (!data.downloads || data.downloads.length === 0)) {
          setDownloadError(
            `Payment is confirmed. Downloads are being prepared. If they do not appear soon, email ${GUIDES_SUPPORT_EMAIL}.`
          );
        }
      } catch (err) {
        if (cancelled) return;
        setDownloadError(err?.message || 'Unable to load downloads right now.');
      } finally {
        if (!cancelled) setLoadingDownloads(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [paymentIntentId, sessionId]);

  const handleBuy = (product) => {
    navigate(`${GUIDES_CHECKOUT_PATH}?product=${encodeURIComponent(product.id)}`, {
      state: { productId: product.id },
    });
  };

  const container = {
    maxWidth: CONTENT_MAX_WIDE,
    margin: '0 auto',
    padding: `0 ${CONTENT_INSET}`,
    width: '100%',
    boxSizing: 'border-box',
  };

  const showDownloads = Boolean(paymentIntentId || sessionId);

  return (
    <div
      className="dd-page dd-guides"
      style={{
        backgroundColor: BG,
        color: TEXT,
        paddingTop: pageTopPadding(isMobile),
      }}
    >
      <Helmet>
        <title>{GUIDES_PAGE_TITLE} | Digital Divot Golf</title>
        <meta
          name="description"
          content="Download Digital Divot's Startup Guide and Equipment & Setup Field Manual. Practical PDF playbooks for launching a mobile golf simulator business."
        />
        <meta property="og:title" content={`${GUIDES_PAGE_TITLE} | Digital Divot Golf`} />
        <meta property="og:description" content={GUIDES_HERO_SUBHEAD} />
        <meta property="og:url" content={`https://digitaldivotgolf.com${GUIDES_PATH}`} />
        <link rel="canonical" href={`https://digitaldivotgolf.com${GUIDES_PATH}`} />
      </Helmet>

      {checkoutNotice ? (
        <div
          role="status"
          style={{
            ...container,
            marginTop: isMobile ? '0.75rem' : '1rem',
            padding: isMobile ? '0.85rem 1rem' : '0.95rem 1.15rem',
            borderRadius: '12px',
            border:
              checkoutNotice.type === 'success'
                ? '1px solid rgba(201, 168, 76, 0.45)'
                : '1px solid rgba(245, 240, 232, 0.2)',
            backgroundColor:
              checkoutNotice.type === 'success'
                ? 'rgba(201, 168, 76, 0.1)'
                : 'rgba(255, 255, 255, 0.04)',
            color: TEXT,
            fontSize: '0.9375rem',
            lineHeight: 1.5,
          }}
        >
          {checkoutNotice.text}
        </div>
      ) : null}

      {showDownloads ? (
        <section id="guide-downloads" style={{ padding: isMobile ? '1.25rem 0 0' : '1.5rem 0 0' }}>
          <div style={container}>
            <div
              style={{
                backgroundColor: CARD_BG,
                border: '1px solid rgba(201, 168, 76, 0.35)',
                borderRadius: '16px',
                padding: isMobile ? '1.2rem' : '1.5rem',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: GOLD,
                }}
              >
                Your Downloads
              </p>
              <h2
                style={{
                  margin: '0.45rem 0 0',
                  fontSize: isMobile ? '1.25rem' : '1.45rem',
                  fontWeight: 800,
                  color: TEXT,
                }}
              >
                {downloadMeta?.productTitle || 'Guide purchase confirmed'}
              </h2>
              <p style={{ margin: '0.55rem 0 0', color: MUTED, fontSize: '0.9rem', lineHeight: 1.55 }}>
                Secure links expire after 72 hours. We also emailed them to{' '}
                {downloadMeta?.customerEmail || 'the address used at checkout'}.
              </p>

              {loadingDownloads ? (
                <p style={{ margin: '1rem 0 0', color: MUTED }}>Preparing your downloads…</p>
              ) : null}

              {downloadError ? (
                <p role="alert" style={{ margin: '1rem 0 0', color: '#E8B4B4', fontSize: '0.9rem' }}>
                  {downloadError}
                </p>
              ) : null}

              {downloads.length > 0 ? (
                <div style={{ marginTop: '1.15rem', display: 'grid', gap: '0.75rem' }}>
                  {downloads.map((item) => (
                    <a
                      key={item.url}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.9rem 1.25rem',
                        borderRadius: '10px',
                        backgroundColor: GOLD,
                        color: '#0A0A0A',
                        fontWeight: 800,
                        fontSize: '0.875rem',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        textDecoration: 'none',
                      }}
                    >
                      <Download size={16} aria-hidden="true" />
                      {item.label || 'Download PDF'}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <section
        style={{
          padding: isMobile
            ? '2rem 0 2.5rem'
            : 'clamp(2.5rem, 6vw, 4rem) 0 clamp(2.5rem, 6vw, 3.5rem)',
          borderBottom: '1px solid rgba(201, 168, 76, 0.12)',
        }}
      >
        <div style={container}>
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '1.25rem' : '2rem',
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo.webp`}
              alt="Digital Divot"
              width={72}
              height={72}
              style={{
                width: isMobile ? '3.25rem' : '4.25rem',
                height: isMobile ? '3.25rem' : '4.25rem',
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
            <div>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: GOLD,
                }}
              >
                {GUIDES_TAGLINE}
              </p>
              <h1
                style={{
                  margin: '0.65rem 0 0',
                  fontSize: isMobile ? '2rem' : 'clamp(2.35rem, 4.2vw, 3.25rem)',
                  fontWeight: 800,
                  color: TEXT,
                  lineHeight: 1.1,
                  letterSpacing: '-0.03em',
                  maxWidth: '18ch',
                }}
              >
                {GUIDES_HERO_HEADLINE}
              </h1>
              <p
                style={{
                  margin: '1.1rem 0 0',
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  color: MUTED,
                  lineHeight: 1.7,
                  maxWidth: '40rem',
                }}
              >
                {GUIDES_HERO_SUBHEAD}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: sectionPadding(isMobile) }}>
        <div style={container}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
              gap: isMobile ? '1rem' : '1.15rem',
              alignItems: 'stretch',
            }}
          >
            {GUIDE_PRODUCTS.map((product) => (
              <GuideProductCard
                key={product.id}
                product={product}
                isMobile={isMobile}
                onBuy={handleBuy}
              />
            ))}
          </div>

          <p
            style={{
              margin: isMobile ? '1.25rem 0 0' : '1.5rem 0 0',
              fontSize: '0.8125rem',
              color: 'rgba(245, 240, 232, 0.55)',
              lineHeight: 1.5,
              textAlign: 'center',
            }}
          >
            {GUIDE_COMING_SOON_NOTE}
          </p>
        </div>
      </section>

      <InflatableInquirySection isMobile={isMobile} />
    </div>
  );
};

export default Guides;
